import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Typography,
  Box,
  Autocomplete,
  Chip
} from '@mui/material';
import { Cancel as CancelIcon, PersonAdd as PersonAddIcon, GroupAdd as GroupAddIcon } from '@mui/icons-material';
import { api } from '@/services/api';
import { playerService } from '@/services/playerService';
import { teamService, Team } from '@/services/teamService';
import { useQueryClient } from 'react-query';
import { useAppSelector } from '@/hooks/redux';
import { OrganizationRole, UserRole } from '@/types/enums';

interface Player {
  id: string;
  name: string;
  parentId: string;
}

interface PlayerAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  type: 'league';
  entityId: string;
  entityName: string;
  onSuccess?: (assignmentData?: { playerId: string; playerName: string; teamName: string }) => void;
}

interface AssignmentFormData {
  playerId: string;
  teamName: string;
  teamId?: string; // Optional team ID for existing teams
}

const PlayerAssignmentDialog: React.FC<PlayerAssignmentDialogProps> = ({
  open,
  onClose,
  type,
  entityId,
  entityName,
  onSuccess
}) => {
  const queryClient = useQueryClient();
  const { user } = useAppSelector((state) => state.auth);
  
  const [players, setPlayers] = useState<Player[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<AssignmentFormData>({
    playerId: '',
    teamName: '',
    teamId: undefined,
  });

  useEffect(() => {
    if (open) {
      fetchPlayers();
      fetchTeams();
      // Reset form
      setFormData({
        playerId: '',
        teamName: '',
        teamId: undefined,
      });
      setError(null);
    }
  }, [open, entityId]);

  const fetchPlayers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/players');
      
      // Filter players based on user permissions
      const isOrgOwnerOrAdmin = user?.orgRole === OrganizationRole.OWNER || user?.orgRole === OrganizationRole.ADMIN;
      const isSuperAdmin = user?.globalRole === UserRole.SUPER_ADMIN;
      
      let filteredPlayers;
      if (isOrgOwnerOrAdmin || isSuperAdmin) {
        // Admins and super admins can see all players in the organization
        filteredPlayers = response.data.players;
      } else {
        // Regular users can only see their own players
        filteredPlayers = response.data.players.filter((player: Player) => 
          player.parentId === user?.parentId
        );
      }
      
      setPlayers(filteredPlayers);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to fetch players');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    if (type !== 'league') return;
    
    try {
      setTeamsLoading(true);
      const teamsData = await teamService.getTeamsByLeague(entityId);
      // Filter out TBD and Bye placeholder teams - not real teams for player assignment
      const filteredTeams = (teamsData || []).filter(team =>
        team.name !== 'TBD' && !team.name.toLowerCase().includes('bye')
      );
      setTeams(filteredTeams);
    } catch (err: any) {
      console.warn('Failed to fetch teams for league:', err);
      // Don't show error for teams - user can still enter manually
      setTeams([]);
    } finally {
      setTeamsLoading(false);
    }
  };

  const handleInputChange = (field: keyof AssignmentFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTeamSelection = (team: Team | null, inputValue?: string) => {
    if (team) {
      // User selected an existing team
      setFormData(prev => ({
        ...prev,
        teamName: team.name,
        teamId: team.id
      }));
    } else if (inputValue !== undefined) {
      // User typed a custom team name
      setFormData(prev => ({
        ...prev,
        teamName: inputValue,
        teamId: undefined
      }));
    }
  };

  const handleSubmit = async () => {
    console.log('🚀 PlayerAssignmentDialog: handleSubmit called with formData:', formData);
    
    try {
      setSubmitting(true);
      setError(null);

      console.log('🔄 PlayerAssignmentDialog: Starting submission process...');

      // Validate required fields
      if (!formData.playerId) {
        throw new Error('Please select a player');
      }
      if (!formData.teamName) {
        throw new Error('Please select or enter a team name');
      }
      if (!entityId) {
        throw new Error(`${type} ID is missing`);
      }

      console.log('Making player assignment request:', {
        type,
        entityId,
        playerId: formData.playerId,
        teamName: formData.teamName,
        teamId: formData.teamId,
      });

      // Use the correct player service method for league assignment
      await playerService.assignToLeague(formData.playerId, {
        leagueId: entityId,
        teamName: formData.teamName,
      });
      console.log('Player assignment successful via player service');

      console.log('🔄 PlayerAssignmentDialog: Team assignment successful, clearing schedule cache...');
      
      // Clear schedule cache to force refresh
      queryClient.removeQueries({
        predicate: (query) => {
          const key = query.queryKey;
          const matches = Array.isArray(key) && key[0] === 'schedule';
          if (matches) {
            console.log('🗑️ PlayerAssignmentDialog: Removing cached schedule query:', key);
          }
          return matches;
        }
      });
      
      console.log('✅ PlayerAssignmentDialog: Schedule cache cleared');
      
      // Get player name for the callback
      const selectedPlayer = players.find(p => p.id === formData.playerId);
      const playerName = selectedPlayer?.name || 'Unknown Player';
      
      onSuccess?.({
        playerId: formData.playerId,
        playerName,
        teamName: formData.teamName
      });
      onClose();
    } catch (err: any) {
      console.error('❌ PlayerAssignmentDialog: Error in handleSubmit:', err);
      console.error('Player assignment error:', err);
      console.error('Error response:', err.response?.data);
      
      let errorMessage = `Failed to assign player to ${type}`;
      if (err.message && !err.response) {
        // Client-side validation error
        errorMessage = err.message;
      } else if (err.response?.data?.error?.message) {
        // Server error
        errorMessage = err.response.data.error.message;
      } else if (err.response?.data?.message) {
        // Alternative server error format
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 404) {
        errorMessage = 'Endpoint not found. Please check if the player and league exist.';
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid request. Please check the form data.';
      }
      
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setError(null);
  };

  // Get team options for autocomplete
  const getTeamOptions = () => {
    return teams.map(team => ({
      label: team.name,
      team: team,
      // Add additional info for better UX
      subtitle: [team.division, team.ageGroup].filter(Boolean).join(' • ')
    }));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableAutoFocus>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupAddIcon />
          Assign Player to League: {entityName}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : players.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No players found. Please create a player first.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Player</InputLabel>
                  <Select
                    value={formData.playerId}
                    onChange={(e) => handleInputChange('playerId', e.target.value)}
                    label="Player"
                  >
                    {players.map((player) => (
                      <MenuItem key={player.id} value={player.id}>
                        {player.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Box>
                  <Autocomplete
                    freeSolo
                    options={getTeamOptions()}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option;
                      return option.label;
                    }}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body1">{option.label}</Typography>
                          {option.subtitle && (
                            <Typography variant="caption" color="text.secondary">
                              {option.subtitle}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Team Name"
                        required
                        helperText={
                          teamsLoading 
                            ? "Loading teams..." 
                            : teams.length > 0 
                              ? "Select an existing team or type a new team name"
                              : "Enter the team name (no existing teams found)"
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {teamsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    value={formData.teamName}
                    onInputChange={(_, newInputValue) => {
                      handleTeamSelection(null, newInputValue);
                    }}
                    onChange={(_, newValue) => {
                      if (typeof newValue === 'object' && newValue?.team) {
                        handleTeamSelection(newValue.team);
                      } else if (typeof newValue === 'string') {
                        handleTeamSelection(null, newValue);
                      }
                    }}
                    loading={teamsLoading}
                    disabled={teamsLoading}
                  />
                  
                  {teams.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Available teams ({teams.length}):
                      </Typography>
                      <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {teams.slice(0, 5).map((team) => (
                          <Chip
                            key={team.id}
                            label={team.name}
                            size="small"
                            variant="outlined"
                            onClick={() => handleTeamSelection(team)}
                            sx={{ cursor: 'pointer' }}
                          />
                        ))}
                        {teams.length > 5 && (
                          <Chip
                            label={`+${teams.length - 5} more`}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  This will assign the selected player to this league with the specified team information.
                  {teams.length > 0 && (
                    <> You can select from existing teams or enter a new team name.</>
                  )}
                </Typography>
              </Grid>
            </Grid>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose} startIcon={<CancelIcon />}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={submitting ? undefined : <PersonAddIcon />}
          disabled={
            !formData.playerId ||
            !formData.teamName ||
            !formData.teamName.trim() ||
            submitting ||
            players.length === 0
          }
        >
          {submitting ? <CircularProgress size={20} /> : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlayerAssignmentDialog;
